import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLocale, useLocalePath, useT } from "../i18n";
import { useApi } from "../lib/api";
import { coverImage, fmt, formatPrice, publicVehicleUrl, siteBase, vehicleTitle } from "../lib/format";
import { whatsappUrl } from "../config/siteConfig";
import { useSiteSettings } from "../lib/siteSettings";
import { Seo } from "../components/Seo";
import { VehicleCard } from "../components/VehicleCard";
import { Gallery } from "../components/vehicle/Gallery";
import { SummaryPanel } from "../components/vehicle/SummaryPanel";
import { useTaxonomies } from "../lib/taxonomies";
import { ConditionPanel, DetailSection, FeaturesList, SpecsList, hasCondition, specRows } from "../components/vehicle/DetailSections";
import { InquiryDialog } from "../components/vehicle/InquiryDialog";
import { StickyBar } from "../components/vehicle/StickyBar";

const AVAILABILITY = { available: "https://schema.org/InStock", reserved: "https://schema.org/LimitedAvailability", sold: "https://schema.org/SoldOut" };

const jsonLd = (v, lang) => {
  const cover = coverImage(v);
  const data = {
    "@context": "https://schema.org",
    "@type": "Car",
    name: `${v.model_year} ${vehicleTitle(v)}`,
    sku: v.stock_id,
    brand: { "@type": "Brand", name: v.make },
    model: v.model,
    vehicleModelDate: String(v.model_year),
    mileageFromOdometer: { "@type": "QuantitativeValue", value: v.mileage_km, unitCode: "KMT" },
    url: publicVehicleUrl(lang, v.slug),
    ...(v.fuel_type && { fuelType: v.fuel_type }),
    ...(v.transmission && { vehicleTransmission: v.transmission }),
    ...(v.exterior_color && { color: v.exterior_color }),
    ...(cover && { image: v.images.slice(0, 5).map((i) => `${siteBase()}${i.variants.large}`) }),
  };
  if (v.price?.amount != null) {
    data.offers = { "@type": "Offer", price: v.price.amount, priceCurrency: "JPY", availability: AVAILABILITY[v.status], url: data.url };
  }
  return JSON.stringify(data);
};

const Message = ({ testId, title, body }) => {
  const t = useT();
  const lp = useLocalePath();
  return (
    <section data-testid={testId} className="flex min-h-[80svh] items-end bg-ink pb-24 pt-40 text-white">
      <Helmet><meta name="robots" content="noindex" /></Helmet>
      <div className="container-x">
        <p className="micro-label text-white/45">{t("nav.inventory")}</p>
        <h1 className="h-section font-display mt-5">{title}</h1>
        <p className="mt-5 max-w-md text-white/60">{body}</p>
        <Link to={lp("inventory")} className="btn btn-light mt-10" data-testid="vehicle-back-inventory">{t("detail.backToInventory")}</Link>
      </div>
    </section>
  );
};

const Skeleton = () => (
  <section className="bg-ink pb-20 pt-28 text-white lg:pt-32" aria-busy="true" data-testid="vehicle-loading">
    <div className="container-x grid gap-10 lg:grid-cols-12">
      <div className="aspect-[3/2] animate-pulse bg-white/5 lg:col-span-8" />
      <div className="space-y-4 lg:col-span-4">
        <div className="h-3 w-32 bg-white/10" />
        <div className="h-10 w-3/4 bg-white/10" />
        <div className="h-8 w-1/2 bg-white/10" />
      </div>
    </div>
  </section>
);

export default function VehicleDetail() {
  const { slug } = useParams();
  const t = useT();
  const lang = useLocale();
  const lp = useLocalePath();
  const { data: v, loading, error } = useApi(`/vehicles/${slug}`);
  const { data: related } = useApi(`/vehicles/${slug}/related`);
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [tx] = useTaxonomies();
  const [inquiry, setInquiry] = useState(false);
  const settings = useSiteSettings();

  useEffect(() => {
    setIndex(0);
    setLightbox(false);
    setInquiry(false);
  }, [slug]);

  if (loading) return <Skeleton />;
  if (error || !v) return <Message testId="vehicle-not-found" title={t("detail.notFoundTitle")} body={t("detail.notFoundBody")} />;

  const title = `${v.model_year} ${vehicleTitle(v)}`;
  const sold = v.status === "sold";
  const similar = new URLSearchParams(Object.entries({ make: v.make, model: v.model, year_from: v.model_year - 2, year_to: v.model_year + 2,
    body_type: v.body_type || "", destination: v.market === "japan" || (v.market === "both" && lang === "ja") ? "japan" : "export" }).filter(([, x]) => x));
  const similarTo = lp(`sourcing?${similar}`);
  const whatsapp = whatsappUrl(settings.whatsapp_number, fmt(t("detail.whatsappMessage"), { title, stock: v.stock_id, url: publicVehicleUrl(lang, v.slug) }));
  const line = settings.line_url || null;
  const overview = lang === "ja" ? v.overview_ja || v.overview_en : v.overview_en || v.overview_ja;
  const specs = specRows(v, t, tx);
  const sections = [
    overview && { id: "overview", title: t("detail.overview"), body: <p className="max-w-2xl whitespace-pre-line text-[16px] leading-relaxed text-ink/80" data-testid="detail-overview-text">{overview}</p> },
    specs.length && { id: "specifications", title: t("detail.specifications"), body: <SpecsList rows={specs} /> },
    v.features?.length && { id: "features", title: t("detail.features"), body: <FeaturesList keys={v.features} /> },
    hasCondition(v.condition) && {
      id: "condition",
      title: t("detail.condition"),
      body: <ConditionPanel condition={v.condition} images={v.images} onOpenPhoto={(i) => { setIndex(i); setLightbox(true); }} />,
    },
  ].filter(Boolean);
  const cover = coverImage(v);
  const hasRelated = related?.length > 0;
  const description = `${formatPrice(v, t)}${v.price?.kind === "japan" && v.price.amount != null ? ` ${t("price.exclTax")}` : ""} · ${v.mileage_km.toLocaleString("en-US")} km · ${v.stock_id}${overview ? ` · ${overview.slice(0, 120)}` : ""}`;

  return (
    <div data-testid="vehicle-detail-page">
      <Seo title={title} description={description} path={`inventory/${v.slug}`} image={cover?.variants.large} />
      <Helmet>
        <script type="application/ld+json">{jsonLd(v, lang)}</script>
      </Helmet>
      <section className="bg-ink pb-16 pt-24 text-white lg:pb-24 lg:pt-28">
        <div className="container-x">
          <nav aria-label="Breadcrumb" className="mb-6 truncate text-xs text-white/45" data-testid="breadcrumb">
            <Link to={lp()} className="hover:text-white">{t("nav.home")}</Link>
            <span className="mx-3">/</span>
            <Link to={lp("inventory")} className="hover:text-white" data-testid="breadcrumb-inventory">{t("nav.inventory")}</Link>
            <span className="mx-3">/</span>
            <span className="text-white/75">{vehicleTitle(v)}</span>
          </nav>
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="min-w-0 lg:col-span-8">
              <Gallery images={v.images} index={index} setIndex={setIndex} lightboxOpen={lightbox} setLightboxOpen={setLightbox} title={title} />
            </div>
            <div className="min-w-0 lg:sticky lg:top-28 lg:col-span-4 lg:self-start">
              <SummaryPanel v={v} onEnquire={() => setInquiry(true)} whatsapp={whatsapp} line={line} shareUrl={publicVehicleUrl(lang, v.slug)} similarTo={similarTo} availableTo={lp("inventory")} />
            </div>
          </div>
        </div>
      </section>
      {sections.length > 0 && (
        <div className="bg-paper text-ink">
          <div className="container-x pb-10 lg:pb-16">
            {sections.map((s, i) => (
              <DetailSection key={s.id} id={s.id} index={String(i + 1).padStart(2, "0")} title={s.title}>{s.body}</DetailSection>
            ))}
          </div>
        </div>
      )}
      {hasRelated && (
        <section className="bg-ink py-20 text-white lg:py-28" data-testid="detail-section-related">
          <div className="container-x">
            <h2 className="font-display text-3xl lg:text-4xl">{t("detail.related")}</h2>
            <div className="mt-12 grid gap-12 sm:grid-cols-2 lg:gap-8 xl:grid-cols-4">
              {related.map((r) => (
                <VehicleCard key={r.id} vehicle={r} maxVariant="medium" sizes="(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw" />
              ))}
            </div>
          </div>
        </section>
      )}
      {!sold && <div className={`h-[76px] lg:hidden ${hasRelated || !sections.length ? "bg-ink" : "bg-paper"}`} aria-hidden />}
      {!sold && (
      <StickyBar hidden={inquiry || lightbox}>
        {(
          <>
            <button type="button" onClick={() => setInquiry(true)} className="btn btn-light flex-1" data-testid="sticky-enquire">{t("detail.enquire")}</button>
            {whatsapp && <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-light flex-1" data-testid="sticky-whatsapp">{t("detail.whatsapp")}</a>}
            {line && <a href={line} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-light flex-1" data-testid="sticky-line">{t("detail.line")}</a>}
          </>
        )}
      </StickyBar>
      )}
      {!sold && <InquiryDialog open={inquiry} onOpenChange={setInquiry} vehicle={v} />}
    </div>
  );
}
